import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { stepLabel } from '../format';
import type { BottleneckStep, FunnelStep } from '../processes.api';

/** Opmaakklassen, geen zichtbare tekst — als constante zodat de i18n-regel ze niet als copy leest. */
const DOT_TONE = {
  empty: 'border-2 border-[var(--border)] bg-[var(--background)] text-[var(--foreground-muted)]',
  fullest: 'bg-[var(--primary)] text-white',
  filled: 'bg-[var(--secondary)] text-white',
} as const;
const BOTTLENECK_RING = 'ring-2 ring-[var(--warning)] ring-offset-2';

/**
 * De stappenbalk uit `mockup-processen-detail.html`: genummerde bollen naast elkaar, met de stapnaam
 * en de telling eronder. Vervangt de smaller-wordende funnel op het detailscherm, maar behoudt wat
 * die wél had en de mockup niet tekent: elke stap blijft aanklikbaar naar de producten in die stap,
 * en een stap met stilstand houdt zijn markering.
 *
 * De eerste stap is de referentie voor "vol": daar staan de meeste producten. Een stap zonder
 * producten krijgt de open bol — niet dezelfde als een gevulde, zodat leeg nooit als klaar leest.
 */
export function ProcesStappenbalk({
  steps,
  bottleneck,
  onStepSelect,
  renderStepExtra,
}: {
  steps: FunnelStep[];
  bottleneck: BottleneckStep | null;
  onStepSelect: (canonicalStep: string) => void;
  renderStepExtra?: (step: FunnelStep) => ReactNode;
}) {
  const { t } = useTranslation();
  const highest = steps.reduce((max, step) => Math.max(max, step.count), 0);

  return (
    <ol className="flex min-w-full items-start gap-1 overflow-x-auto" data-testid="proces-stappenbalk">
      {steps.map((step, index) => {
        const isBottleneck = bottleneck?.canonicalStep === step.canonicalStep;
        const dotTone = step.count === 0 ? DOT_TONE.empty : step.count === highest ? DOT_TONE.fullest : DOT_TONE.filled;

        return (
          <li className="flex min-w-[96px] flex-1 flex-col items-center gap-1.5 px-0.5" data-testid={`stappenbalk-stap-${step.canonicalStep}`} key={step.canonicalStep}>
            <button
              aria-label={t('processes.detail.stepSelect', { step: stepLabel(step.canonicalStep, t), count: step.count })}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--secondary)] ${dotTone} ${isBottleneck ? BOTTLENECK_RING : ''}`}
              data-testid={`stappenbalk-knop-${step.canonicalStep}`}
              onClick={() => onStepSelect(step.canonicalStep)}
              type="button"
            >
              {index + 1}
            </button>
            <span className="min-h-[28px] text-center text-[11px] leading-tight text-[var(--foreground-muted)]">{stepLabel(step.canonicalStep, t)}</span>
            <span className="text-sm font-semibold text-[var(--foreground)]">{step.count}</span>
            {renderStepExtra?.(step)}
          </li>
        );
      })}
    </ol>
  );
}
